import { useEffect, useState } from "react";
import { useLang } from "@/lib/i18n";
import { whatsappUrl } from "@/lib/translations";

export const Header = () => {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const nav = [
    { id: "work", href: "#work", label: t.nav.work },
    { id: "services", href: "#services", label: t.nav.services },
    { id: "process", href: "#process", label: t.nav.process },
    { id: "contact", href: "#contact", label: t.nav.contact },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        scrolled
          ? "bg-white/85 backdrop-blur-md border-b border-neutral-200"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="flex items-center justify-between h-16 md:h-20">
          <a
            href="#top"
            data-testid="logo-link"
            className="flex items-baseline gap-1 group"
          >
            <span className="font-serif text-2xl md:text-[28px] tracking-tight leading-none">
              Girona
            </span>
            <span className="font-serif italic text-2xl md:text-[28px] tracking-tight leading-none text-neutral-500">
              Webs
            </span>
            <span className="ml-1 mt-[6px] w-1.5 h-1.5 rounded-full bg-emerald-500" />
          </a>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-10">
            {nav.map((n) => (
              <a
                key={n.id}
                href={n.href}
                data-testid={`nav-${n.id}`}
                className="text-[13px] tracking-wide text-neutral-700 hover:text-black transition-colors"
              >
                {n.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3 md:gap-5">
            <div
              data-testid="lang-switcher"
              className="flex items-center gap-1 font-mono text-[11px] tracking-wider"
            >
              <button
                data-testid="lang-es"
                onClick={() => setLang("es")}
                className={`px-1.5 py-1 transition-colors ${
                  lang === "es" ? "text-black" : "text-neutral-400 hover:text-neutral-700"
                }`}
              >
                ES
              </button>
              <span className="text-neutral-300">/</span>
              <button
                data-testid="lang-ca"
                onClick={() => setLang("ca")}
                className={`px-1.5 py-1 transition-colors ${
                  lang === "ca" ? "text-black" : "text-neutral-400 hover:text-neutral-700"
                }`}
              >
                CA
              </button>
            </div>

            <a
              data-testid="header-cta"
              href={whatsappUrl(lang)}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:inline-flex items-center gap-2 px-4 py-2.5 bg-black text-white text-[12px] tracking-[0.15em] uppercase hover:bg-neutral-800 transition-colors"
            >
              <span>{t.nav.cta}</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M5 12h14M13 5l7 7-7 7" />
              </svg>
            </a>

            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileOpen((v) => !v)}
              className="lg:hidden p-2 -mr-2"
              aria-label="Menu"
            >
              <div className="w-6 h-[1px] bg-black mb-1.5" />
              <div className={`w-6 h-[1px] bg-black transition-transform ${mobileOpen ? "translate-x-2" : ""}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-white border-t border-neutral-200"
        >
          <nav className="px-6 py-6 flex flex-col gap-5">
            {nav.map((n) => (
              <a
                key={n.id}
                href={n.href}
                data-testid={`mobile-nav-${n.id}`}
                onClick={() => setMobileOpen(false)}
                className="font-serif text-2xl text-black"
              >
                {n.label}
              </a>
            ))}
            <a
              data-testid="mobile-cta"
              href={whatsappUrl(lang)}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center justify-center gap-2 px-4 py-3 bg-black text-white text-[12px] tracking-[0.15em] uppercase"
            >
              {t.nav.cta}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};
